import { useState, useEffect } from 'react';
import {
  collection,
  collectionGroup,
  query,
  where,
  orderBy,
  onSnapshot,
  limit,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from 'src/config/firebase';
import { ChatMessage } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/Messages/DashboardChatDetails';

export interface InquiryItem {
  chatId: string;
  message: ChatMessage;
}

export const useInquiriesRealtime = (trainerUid: string) => {
  const [inquiries, setInquiries] = useState<InquiryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const [activeUid, setActiveUid] = useState(
    () => auth.currentUser?.uid || (trainerUid && !trainerUid.includes('@') ? trainerUid : ''),
  );

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user?.uid) {
        setActiveUid(user.uid);
      } else {
        setIsLoading(false);
      }
    });
    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    const uidToUse =
      activeUid ||
      auth.currentUser?.uid ||
      (trainerUid && !trainerUid.includes('@') ? trainerUid : '');

    if (!uidToUse) {
      return;
    }

    setIsLoading(true);

    // Ensure we have a composite index for this:
    // collectionGroup: messages
    // fields: receive_uid (ASC), message_type (ASC), send_time (DESC)
    const clientQuery = query(
      collectionGroup(db, 'messages'),
      where('receive_uid', '==', uidToUse),
      where('message_type', '==', 'inquiry'),
      orderBy('send_time', 'desc'),
      limit(100),
    );

    let clientItems: InquiryItem[] = [];
    let supportItems: InquiryItem[] = [];

    const updateCombined = () => {
      const combined = [...clientItems, ...supportItems];
      combined.sort((a, b) => {
        const timeA = a.message.send_time
          ? new Date(
              typeof (a.message.send_time as any).toDate === 'function'
                ? (a.message.send_time as any).toDate()
                : a.message.send_time,
            ).getTime()
          : 0;
        const timeB = b.message.send_time
          ? new Date(
              typeof (b.message.send_time as any).toDate === 'function'
                ? (b.message.send_time as any).toDate()
                : b.message.send_time,
            ).getTime()
          : 0;
        return timeB - timeA;
      });
      setInquiries(combined);
      setIsLoading(false);
      setError(null);
    };

    const unsubscribeClient = onSnapshot(
      clientQuery,
      (snapshot) => {
        const items: InquiryItem[] = [];
        snapshot.docs.forEach((docSnap) => {
          const chatId = docSnap.ref.parent.parent?.id;
          const data = docSnap.data() as ChatMessage;

          if (chatId) {
            items.push({
              chatId,
              message: { ...data, id: docSnap.id },
            });
          }
        });
        clientItems = items;
        updateCombined();
      },
      (err) => {
        console.error('Error fetching client inquiries:', err);
        setError(err);
        setIsLoading(false);
      },
    );

    // Support chat inquiries listener
    const supportChatId = `support_${uidToUse}`;
    const supportQuery = query(
      collection(db, 'flutter_chat_messages', supportChatId, 'messages'),
      orderBy('send_time', 'desc'),
      limit(50),
    );

    const unsubscribeSupport = onSnapshot(
      supportQuery,
      (snapshot) => {
        const items: InquiryItem[] = [];
        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data() as ChatMessage;
          if (data.is_support || data.message_type === 'support' || data.subject) {
            items.push({
              chatId: supportChatId,
              message: {
                ...data,
                id: docSnap.id,
                inquiryType: data.topic || (data as any).inquiryType || 'Billing & Subscription',
                inquirySubject: data.subject || (data as any).inquirySubject || 'Support Request',
                inquiryDetail: data.message || data.body || '',
                sender_name:
                  data.sender_role === 'trainer' ||
                  data.sender_uid === uidToUse ||
                  data.send_uid === uidToUse
                    ? data.sender_name || 'You'
                    : 'miniPT Support',
                is_support: true,
              },
            });
          }
        });
        supportItems = items;
        updateCombined();
      },
      () => {
        supportItems = [];
        updateCombined();
      },
    );

    return () => {
      unsubscribeClient();
      unsubscribeSupport();
    };
  }, [trainerUid, activeUid]);

  return { inquiries, isLoading, error };
};
